import React from 'react';

const EULA: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl text-white">
        <div className="bg-black/40 border border-gold-500/20 rounded-lg shadow-gold p-6 lg:p-8">
          <h1 className="text-4xl font-display text-gold-500 mb-8">End User License Agreement (EULA)</h1>
          <h2 className="text-2xl font-display text-gold-400 mb-4">Divine Intelligence Application</h2>
          
          <p className="mb-8 text-gray-300">
            Last Updated: January 19, 2025
          </p>

          <p className="mb-8 text-gray-300">
            PLEASE READ THIS END USER LICENSE AGREEMENT ("AGREEMENT") CAREFULLY BEFORE USING THE DIVINE INTELLIGENCE APPLICATION ("THE APPLICATION"). 
            BY USING THE APPLICATION, YOU AGREE TO BE BOUND BY THE TERMS OF THIS AGREEMENT.
          </p>

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">1. License Grant and Restrictions</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">1.1. Grant of License</h3>
            <p className="mb-4 text-gray-300">
              Subject to the terms of this Agreement, Divine Intelligence grants you a limited, non-exclusive, non-transferable, 
              revocable license to use the Application for your personal, non-commercial use.
            </p>

            <h3 className="text-xl font-display text-gold-300 mb-3">1.2. Restrictions</h3>
            <p className="mb-2 text-gray-300">You shall NOT:</p>
            <ul className="list-disc ml-6 mb-4 text-gray-300 space-y-2">
              <li>Modify, reverse engineer, decompile, or disassemble the Application</li>
              <li>Copy or reproduce any part of the Application</li>
              <li>Distribute, lease, license, sell, rent, or otherwise transfer the Application</li>
              <li>Remove any proprietary notices or labels on the Application</li>
              <li>Use the Application for any illegal or unauthorized purpose</li>
            </ul>

            <h3 className="text-xl font-display text-gold-300 mb-3">1.3. License Transfer</h3>
            <p className="mb-4 text-gray-300">
              The license granted under this Agreement is personal to you and may not be transferred to any third party 
              without prior written consent from Divine Intelligence.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">2. Intellectual Property</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">2.1. Ownership</h3>
            <p className="mb-4 text-gray-300">
              The Application, including all intellectual property rights, is owned by Divine Intelligence. 
              This Agreement does not grant you any rights to patents, copyrights, trade secrets, trademarks, 
              or any other rights in respect to the Application.
            </p>

            <h3 className="text-xl font-display text-gold-300 mb-3">2.2. Third-Party Content</h3>
            <ul className="list-disc ml-6 mb-4 text-gray-300 space-y-2">
              <li>Bible Translations: Various Bible translations available in the Application may be protected by copyright. 
                  Usage is subject to respective translation copyright holders' terms.</li>
              <li>AI-Generated Content: Content generated through AI features is provided "as is" without any warranties.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">3. Data Collection and Privacy</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">3.1. User Data</h3>
            <p className="mb-2 text-gray-300">The Application collects and processes:</p>
            <ul className="list-disc ml-6 mb-4 text-gray-300 space-y-2">
              <li>User account information</li>
              <li>Usage analytics</li>
              <li>Study notes and preferences</li>
              <li>AI interaction data</li>
            </ul>

            <h3 className="text-xl font-display text-gold-300 mb-3">3.2. Data Storage</h3>
            <ul className="list-disc ml-6 mb-4 text-gray-300 space-y-2">
              <li>Local Storage: Some data is stored locally on your device</li>
              <li>Cloud Storage: User data may be synchronized with our servers</li>
              <li>Third-party Services: Data may be processed through third-party services (e.g., Hugging Face, Supabase)</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">4. User Content and Conduct</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">4.1. User-Generated Content</h3>
            <p className="mb-4 text-gray-300">
              You retain ownership of any content you create using the Application (e.g., notes, annotations). 
              You grant Divine Intelligence a non-exclusive license to store and process this content.
            </p>

            <h3 className="text-xl font-display text-gold-300 mb-3">4.2. Acceptable Use</h3>
            <p className="mb-2 text-gray-300">You agree not to use the Application to:</p>
            <ul className="list-disc ml-6 mb-4 text-gray-300 space-y-2">
              <li>Violate any laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Distribute harmful or malicious content</li>
              <li>Interfere with other users' use of the Application</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">5. AI Features and Limitations</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">5.1. AI Services</h3>
            <ul className="list-disc ml-6 mb-4 text-gray-300 space-y-2">
              <li>The Application uses AI models for scripture analysis and insights</li>
              <li>AI-generated content is provided for reference only</li>
              <li>No guarantee of accuracy or completeness is provided</li>
            </ul>

            <h3 className="text-xl font-display text-gold-300 mb-3">5.2. Usage Limits</h3>
            <ul className="list-disc ml-6 mb-4 text-gray-300 space-y-2">
              <li>AI features may have rate limits</li>
              <li>Service availability may vary based on network conditions</li>
              <li>Features may be modified or discontinued at any time</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">6. Warranty Disclaimer</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">6.1. Disclaimer</h3>
            <p className="mb-4 text-gray-300">
              THE APPLICATION IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO 
              WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <h3 className="text-xl font-display text-gold-300 mb-3">6.2. No Warranties</h3>
            <p className="mb-2 text-gray-300">Divine Intelligence does not warrant that:</p>
            <ul className="list-disc ml-6 mb-4 text-gray-300 space-y-2">
              <li>The Application will meet your requirements</li>
              <li>The Application will be uninterrupted or error-free</li>
              <li>AI-generated content will be accurate or suitable for any purpose</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">7. Limitation of Liability</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">7.1. Liability Limitation</h3>
            <p className="mb-4 text-gray-300">
              IN NO EVENT SHALL DIVINE INTELLIGENCE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR 
              CONSEQUENTIAL DAMAGES ARISING OUT OF OR IN CONNECTION WITH THE APPLICATION.
            </p>

            <h3 className="text-xl font-display text-gold-300 mb-3">7.2. Maximum Liability</h3>
            <p className="mb-4 text-gray-300">
              Total liability for any claims relating to the Application shall not exceed the amount paid by you 
              for the Application (if any).
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">8. Termination</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">8.1. Term</h3>
            <p className="mb-4 text-gray-300">
              This Agreement is effective until terminated by you or Divine Intelligence.
            </p>

            <h3 className="text-xl font-display text-gold-300 mb-3">8.2. Termination Rights</h3>
            <p className="mb-4 text-gray-300">
              Divine Intelligence may terminate this Agreement at any time without notice if you fail to comply with any term(s).
            </p>

            <h3 className="text-xl font-display text-gold-300 mb-3">8.3. Effects of Termination</h3>
            <ul className="list-disc ml-6 mb-4 text-gray-300 space-y-2">
              <li>Your license to use the Application ends</li>
              <li>You must cease all use of the Application</li>
              <li>You must delete all copies of the Application</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">9. Updates and Modifications</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">9.1. Rights Reserved</h3>
            <p className="mb-2 text-gray-300">Divine Intelligence reserves the right to:</p>
            <ul className="list-disc ml-6 mb-4 text-gray-300 space-y-2">
              <li>Update the Application</li>
              <li>Modify this Agreement</li>
              <li>Change or discontinue any features or services</li>
            </ul>

            <h3 className="text-xl font-display text-gold-300 mb-3">9.2. Acceptance</h3>
            <p className="mb-4 text-gray-300">
              Continued use after changes constitutes acceptance of modifications.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">10. Governing Law</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">10.1. Jurisdiction</h3>
            <p className="mb-4 text-gray-300">
              This Agreement shall be governed by the laws of Oklahoma.
            </p>

            <h3 className="text-xl font-display text-gold-300 mb-3">10.2. Venue</h3>
            <p className="mb-4 text-gray-300">
              Any disputes shall be subject to the exclusive jurisdiction of the courts of Oklahoma.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">11. Contact Information</h2>
            <p className="mb-4 text-gray-300">
              For questions about this Agreement, contact: Divineintelligenceapp.com
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">12. Indemnification</h2>
            <p className="mb-4 text-gray-300">
              You agree to indemnify, defend, and hold harmless Divine Intelligence, its affiliates, and licensors from and against 
              any and all claims, liabilities, damages, losses, or expenses (including legal fees) arising out of or in any way 
              connected with your violation of this Agreement, use of the Application, or infringement on any third-party rights.
            </p>
          </section>

          {/* Section 13 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">13. Export Compliance</h2>
            <p className="mb-4 text-gray-300">
              You agree to comply with all applicable export and re-export control laws and regulations, including, but not limited 
              to, the Export Administration Regulations (EAR) and sanctions programs administered by the Office of Foreign Assets 
              Control (OFAC). You shall not — directly or indirectly — use, distribute, transfer, or transmit any part of the 
              Application in violation of such laws or regulations.
            </p>
          </section>

          {/* Section 14 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">14. Force Majeure</h2>
            <p className="mb-4 text-gray-300">
              Divine Intelligence shall not be liable for any failure to perform its obligations under this Agreement due to causes 
              beyond its reasonable control, including but not limited to natural disasters, wars, terrorism, labor disputes, 
              internet failures, or government actions.
            </p>
          </section>

          {/* Section 15 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">15. Severability</h2>
            <p className="mb-4 text-gray-300">
              If any provision of this Agreement is held to be invalid or unenforceable by a court of competent jurisdiction, 
              the remaining provisions of this Agreement will remain in full force and effect.
            </p>
          </section>

          {/* Section 16 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">16. Entire Agreement</h2>
            <p className="mb-4 text-gray-300">
              This Agreement constitutes the entire agreement between you and Divine Intelligence concerning the subject matter 
              hereof, and it supersedes any prior agreements, representations, or understandings.
            </p>
          </section>

          {/* Section 17 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">17. No Waiver</h2>
            <p className="mb-4 text-gray-300">
              Failure of Divine Intelligence to enforce any right or provision of this Agreement shall not constitute a waiver 
              of such right or provision.
            </p>
          </section>

          {/* Section 18 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">18. Third-Party Software and Integrations</h2>
            <p className="mb-4 text-gray-300">
              The Application may integrate or interoperate with third-party software or services. Any use of third-party 
              software is subject to the terms of service and privacy policies of such third parties. Divine Intelligence is 
              not responsible for the content, functionality, or privacy practices of third-party services.
            </p>
          </section>

          {/* Section 19 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">19. Children's Online Privacy Protection Act (COPPA)</h2>
            <p className="mb-4 text-gray-300">
              The Application requires users to be 13 years of age or older, or to have verifiable parental consent if required 
              under applicable law.
            </p>
          </section>

          {/* Section 20 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">20. Binding Arbitration and Class Action Waiver</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">20.1. Arbitration</h3>
            <p className="mb-4 text-gray-300">
              Any dispute, controversy, or claim arising out of or relating to this Agreement or the breach thereof shall be 
              settled by binding arbitration administered by [arbitration body] in accordance with its rules.
            </p>

            <h3 className="text-xl font-display text-gold-300 mb-3">20.2. Class Action Waiver</h3>
            <p className="mb-4 text-gray-300">
              You agree that any arbitration or legal action will be conducted solely on an individual basis, and not as a 
              class action or consolidated arbitration.
            </p>
          </section>

          {/* Section 21 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">21. Beta Testing or Early Access</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">21.1. Early Access or Beta Versions</h3>
            <p className="mb-4 text-gray-300">
              If you are using a beta version or early access version of the Application, you acknowledge that such versions 
              are provided on an "as-is" basis, and may contain bugs or incomplete features. Divine Intelligence makes no 
              guarantees regarding the stability or functionality of beta versions, and they may be substantially modified 
              or discontinued at any time without notice.
            </p>
          </section>

          {/* Section 22 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">22. Refund Policy</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">22.1. All Sales Final</h3>
            <p className="mb-4 text-gray-300">
              All sales of the Application or any related services are final. Divine Intelligence does not provide refunds 
              or credits for any purchases, except where required by applicable law.
            </p>
          </section>

          {/* Section 23 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">23. Support and Maintenance</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">23.1. No Obligation for Support</h3>
            <p className="mb-4 text-gray-300">
              Divine Intelligence may, at its sole discretion, provide updates, bug fixes, or maintenance for the Application. 
              However, Divine Intelligence is under no obligation to provide ongoing support, updates, or improvements to 
              the Application.
            </p>
          </section>

          {/* Section 24 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">24. Data Deletion Requests</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">24.1. User Rights to Data Deletion</h3>
            <p className="mb-4 text-gray-300">
              In accordance with applicable data protection laws, including but not limited to the General Data Protection 
              Regulation (GDPR) and the California Consumer Privacy Act (CCPA), you may request the deletion of your personal 
              data by contacting Divine Intelligence at Divineintelligenceapp.com. Divine Intelligence will 
              process such requests in compliance with the relevant data protection regulations.
            </p>
          </section>

          {/* Section 25 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">25. User Feedback</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">25.1. Feedback and Suggestions</h3>
            <p className="mb-4 text-gray-300">
              By providing any feedback, suggestions, or ideas to Divine Intelligence, you agree that Divine Intelligence 
              shall have full ownership of such feedback and may use it for any purpose without any obligation to compensate 
              you. You waive any claim to rights in such feedback, including intellectual property rights.
            </p>
          </section>

          {/* Section 26 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">26. Jurisdiction for Dispute Resolution</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">26.1. International Disputes</h3>
            <p className="mb-4 text-gray-300">
              For users outside of Oklahoma, this Agreement shall be governed by the laws of Oklahoma, and any disputes shall 
              be resolved in the courts of Oklahoma, unless otherwise required by local laws of your country.
            </p>
          </section>

          {/* Section 27 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">27. Limitation of AI Use</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">27.1. AI Usage</h3>
            <p className="mb-4 text-gray-300">
              The Application's AI-generated content is for informational purposes only. Divine Intelligence is not responsible 
              for any decisions or actions taken based on this information.
            </p>
          </section>

          {/* Section 28 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">28. Age Requirements</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">28.1. Age Verification</h3>
            <p className="mb-4 text-gray-300">
              By using the Application, you confirm that you are at least 13 years of age or have received verifiable 
              parental consent if required under applicable law.
            </p>
          </section>

          {/* Section 29 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">29. License Fees and Payment Terms</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">29.1. Payment Terms</h3>
            <p className="mb-4 text-gray-300">
              If you purchase any services or features within the Application, you agree to pay all applicable fees and taxes. 
              Payment terms will be specified at the time of purchase.
            </p>
          </section>

          {/* Section 30 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">30. Subscription Services</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">30.1. Subscription Terms</h3>
            <p className="mb-4 text-gray-300">
              If you subscribe to any services within the Application, your subscription will automatically renew unless 
              cancelled according to the terms specified at the time of purchase.
            </p>
          </section>

          {/* Section 31 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">31. Third-Party Liability</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">31.1. Third-Party Liability</h3>
            <p className="mb-4 text-gray-300">
              Divine Intelligence is not responsible for the actions or failures of any third-party service providers used 
              by the Application. This includes data breaches, downtime, or data loss caused by third-party platforms.
            </p>
          </section>

          {/* Section 32 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">32. Mandatory Updates</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">32.1. Update Requirements</h3>
            <p className="mb-4 text-gray-300">
              Divine Intelligence may, at its discretion, require you to install updates or patches to continue using the 
              Application. Failure to install updates may result in limited functionality or termination of your license.
            </p>
          </section>

          {/* Section 33 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">33. Security Measures</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">33.1. Security Responsibilities</h3>
            <p className="mb-4 text-gray-300">
              Divine Intelligence takes reasonable measures to protect user data, but you acknowledge that no system is 
              completely secure. Users are responsible for safeguarding their login credentials and ensuring their devices 
              are secure.
            </p>
          </section>

          {/* Section 34 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">34. Beta Feedback Clarification</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">34.1. Beta Feedback Usage</h3>
            <p className="mb-4 text-gray-300">
              Any feedback, including bug reports or suggestions provided during the beta testing phase, may be used by 
              Divine Intelligence without obligation to compensate the user.
            </p>
          </section>

          {/* Section 35 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">35. User Responsibilities</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">35.1. Technical Requirements</h3>
            <p className="mb-4 text-gray-300">
              Users are responsible for maintaining compatible hardware and software to use the Application, including 
              installing updates as required by Divine Intelligence.
            </p>
          </section>

          {/* Section 36 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">36. License Scope</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">36.1. Multi-Device Usage</h3>
            <p className="mb-4 text-gray-300">
              The license granted to you applies to multiple devices owned by you but may not be shared across multiple users.
            </p>
          </section>

          {/* Section 37 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">37. In-App Purchases</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">37.1. Purchase Terms</h3>
            <p className="mb-4 text-gray-300">
              Purchases made within the Application, including but not limited to subscriptions and microtransactions, are 
              subject to applicable app store policies and are non-refundable unless required by law.
            </p>
          </section>

          {/* Section 38 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">38. Advertising and Marketing</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">38.1. Third-Party Advertising</h3>
            <p className="mb-4 text-gray-300">
              The Application may display advertisements. You agree that Divine Intelligence is not responsible for the 
              accuracy or content of third-party advertisements.
            </p>
          </section>

          {/* Section 39 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">39. Third-Party APIs and Services</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">39.1. API Liability</h3>
            <p className="mb-4 text-gray-300">
              Divine Intelligence disclaims responsibility for the actions or data provided by third-party APIs or services 
              accessed through the Application.
            </p>
          </section>

          {/* Section 40 */}
          <section className="mb-8">
            <h2 className="text-2xl font-display text-gold-400 mb-4">40. Error Reporting</h2>
            
            <h3 className="text-xl font-display text-gold-300 mb-3">40.1. Bug Reporting</h3>
            <p className="mb-4 text-gray-300">
              Users are encouraged to report any bugs or issues encountered in the Application. Divine Intelligence will 
              attempt to address reported issues but makes no guarantees on resolution times.
            </p>
          </section>

          <p className="mb-8 text-gray-300">
            By using the Application, you acknowledge that you have read this Agreement, understand it, and agree to be 
            bound by its terms and conditions.
          </p>

          <div className="text-sm text-gray-400 border-t border-gold-500/20 pt-4 mt-8">
            <p>Last updated: January 19, 2025</p>
            <p className="mt-2">
              For any questions about this EULA, please contact us at:{' '}
              <a href="mailto:support@divineintelligence.app" className="text-gold-500 hover:text-gold-400 underline">
                support@divineintelligence.app
              </a>
            </p>
          </div>
        </div>
      </div>
  );
};

export default EULA;
